import { type ReactNode, createContext, useContext, useEffect, useState } from 'react';

import type { Crumb } from '../navigation/nav-items';

interface HeaderCrumbsValue {
  extra: readonly Crumb[];
  setExtra: (crumbs: readonly Crumb[]) => void;
}

const NONE: readonly Crumb[] = [];
const HeaderCrumbsContext = createContext<HeaderCrumbsValue>({
  extra: NONE,
  setExtra: () => undefined,
});

/**
 * Holds the crumbs a page adds below its sidebar trail (a team's name under Teams & Stats). The
 * trail renders in the header (DESIGN-V3), while the page knows the deeper crumbs: `PageHeader`
 * hands them up with `usePageCrumbs`.
 */
export function HeaderCrumbsProvider({ children }: { children: ReactNode }) {
  const [extra, setExtra] = useState<readonly Crumb[]>(NONE);
  return (
    <HeaderCrumbsContext.Provider value={{ extra, setExtra }}>
      {children}
    </HeaderCrumbsContext.Provider>
  );
}

export function useHeaderCrumbs(): readonly Crumb[] {
  return useContext(HeaderCrumbsContext).extra;
}

/** Shows `crumbs` after the sidebar trail while the calling page is mounted. */
export function usePageCrumbs(crumbs: readonly Crumb[] = NONE) {
  const { setExtra } = useContext(HeaderCrumbsContext);
  // A string key: pages pass a new array each render, the crumbs rarely change.
  const key = JSON.stringify(crumbs);
  useEffect(() => {
    setExtra(JSON.parse(key) as Crumb[]);
    return () => setExtra(NONE);
  }, [key, setExtra]);
}
